import React, { useState, useRef } from "react";
import { useFocusReturn } from "../hooks/useFocusReturn";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Layout from "../components/Layout";
import { EmptyState } from "../components/common/EmptyState";
import { useTenantQuery } from "../hooks/useTenants";
import { useImpersonation } from "../hooks/useImpersonation";
import {
  useBillingSubscriptionsQuery,
  useBillingOrdersQuery,
} from "../hooks/useAdminProductCatalog";
import {
  SubscriptionDetailModal,
  OrderDetailModal,
  CreateOrderModal,
  TenantMigrationModal,
  SubscriptionRow,
  SubscriptionTableHead,
  OrderRow,
  OrderTableHead,
} from "./admin/billing/components";
import {
  getTenantUsers,
  getTenantHotels,
  TenantMember,
  TenantHotel,
  type Tenant,
} from "../services/tenantApi";
import type { BillingSubscription, BillingOrder } from "../types/adminBilling";
import { TenantBasicInfoForm } from "../components/tenants/TenantBasicInfoForm";
import { CustomDomainsManager } from "../components/tenants/CustomDomainsManager";
import { ModuleManager } from "../components/tenants/ModuleManager";
import { MarketingSalesForm } from "../components/tenants/MarketingSalesForm";

type TabType = "users" | "hotels" | "subscriptions" | "orders" | "marketing" | "settings";

const PAGE_LIMIT = 20;

const TenantDetails: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("users");

  const { data: tenant, isLoading: isTenantLoading } = useTenantQuery(
    tenantId || ""
  );

  const { data: users, isLoading: isUsersLoading } = useQuery({
    queryKey: ["tenant-users", tenantId],
    queryFn: () => getTenantUsers(tenantId!),
    enabled: !!tenantId && activeTab === "users",
  });

  const { data: hotels, isLoading: isHotelsLoading } = useQuery({
    queryKey: ["tenant-hotels", tenantId],
    queryFn: () => getTenantHotels(tenantId!),
    enabled: !!tenantId && activeTab === "hotels",
  });

  // AC-S3, AC-C1, AC-C10: read billing subscriptions from billing endpoint
  // (lazy fetch — only when the Subscriptions tab is active).
  const subscriptionsQuery = useBillingSubscriptionsQuery(
    tenant?.brandId ?? null,
    {
      tenantId: tenant?.id,
      limit: PAGE_LIMIT,
      offset: 0,
      enabled: activeTab === "subscriptions",
    }
  );

  // AC-O3, AC-O4: orders tab analogous read.
  const ordersQuery = useBillingOrdersQuery(tenant?.brandId ?? null, {
    tenantId: tenant?.id,
    limit: PAGE_LIMIT,
    offset: 0,
    enabled: activeTab === "orders",
  });

  const { startImpersonation, isStarting, canImpersonate } = useImpersonation();

  if (!tenantId) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-red-600">Brak ID tenanta</p>
        </div>
      </Layout>
    );
  }

  if (isTenantLoading) {
    return (
      <Layout>
        <div className="p-6 flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </Layout>
    );
  }

  if (!tenant) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-red-600">Tenant nie znaleziony</p>
        </div>
      </Layout>
    );
  }

  // AC-O3: Orders tab between Subscriptions and Settings.
  const tabs = [
    { id: "users" as TabType, label: "Uzytkownicy", count: users?.length },
    { id: "hotels" as TabType, label: "Hotele", count: hotels?.length },
    {
      id: "subscriptions" as TabType,
      label: "Subskrypcje",
      // AC-C1: total from paginated response, not undefined or hardcoded 0.
      count: subscriptionsQuery.data?.total,
    },
    {
      id: "orders" as TabType,
      label: "Zamówienia",
      count: ordersQuery.data?.total,
    },
    { id: "marketing" as TabType, label: "Marketing i sprzedaż" },
    { id: "settings" as TabType, label: "Ustawienia" },
  ];

  const handleImpersonate = (userId: string) => {
    if (canImpersonate) {
      startImpersonation(userId);
    }
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/admin/tenants")}
            className="text-primary-700 hover:text-primary-800 flex items-center gap-1 mb-4"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Wstecz do listy tenantow
          </button>

          <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
          <p className="text-gray-500">{tenant.subdomain}.hotelb2b.com</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav
            className="-mb-px flex space-x-8"
            aria-label="Tabs"
            role="tablist"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`${tab.id}-tab`}
                aria-selected={activeTab === tab.id}
                aria-controls={`${tab.id}-panel`}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab.id
                      ? "border-primary-500 text-primary-700"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }
                `}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span
                    className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                      activeTab === tab.id
                        ? "bg-primary-100 text-primary-700"
                        : "bg-gray-100 text-gray-900"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "users" && (
          <div
            role="tabpanel"
            id="users-panel"
            aria-labelledby="users-tab"
            tabIndex={0}
            aria-live="polite"
            aria-atomic="false"
          >
            <UsersTab
              users={users || []}
              isLoading={isUsersLoading}
              onImpersonate={handleImpersonate}
              canImpersonate={canImpersonate}
              isImpersonating={isStarting}
            />
          </div>
        )}

        {activeTab === "hotels" && (
          <div
            role="tabpanel"
            id="hotels-panel"
            aria-labelledby="hotels-tab"
            tabIndex={0}
            aria-live="polite"
            aria-atomic="false"
          >
            <HotelsTab hotels={hotels || []} isLoading={isHotelsLoading} />
          </div>
        )}

        {activeTab === "subscriptions" && (
          <div
            role="tabpanel"
            id="subscriptions-panel"
            aria-labelledby="subscriptions-tab"
            tabIndex={0}
            aria-live="polite"
            aria-atomic="false"
          >
            <SubscriptionsTab tenant={tenant} query={subscriptionsQuery} />
          </div>
        )}

        {activeTab === "orders" && (
          <div
            role="tabpanel"
            id="orders-panel"
            aria-labelledby="orders-tab"
            tabIndex={0}
            aria-live="polite"
            aria-atomic="false"
          >
            <OrdersTab tenant={tenant} query={ordersQuery} />
          </div>
        )}

        {activeTab === "marketing" && (
          <div
            role="tabpanel"
            id="marketing-panel"
            aria-labelledby="marketing-tab"
            tabIndex={0}
            aria-live="polite"
            aria-atomic="false"
          >
            <MarketingTab tenant={tenant} />
          </div>
        )}

        {activeTab === "settings" && (
          <div
            role="tabpanel"
            id="settings-panel"
            aria-labelledby="settings-tab"
            tabIndex={0}
            aria-live="polite"
            aria-atomic="false"
          >
            <SettingsTab tenant={tenant} />
          </div>
        )}
      </div>
    </Layout>
  );
};

// Users Tab Component
const UsersTab: React.FC<{
  users: TenantMember[];
  isLoading: boolean;
  onImpersonate: (userId: string) => void;
  canImpersonate: boolean;
  isImpersonating: boolean;
}> = ({ users, isLoading, onImpersonate, canImpersonate, isImpersonating }) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Brak uzytkownikow w tym tenancie
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Uzytkownik
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Rola
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Dolaczyl
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Akcje
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-700 font-medium">
                      {user.firstName?.[0] || user.email[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.tenantRole === "tenant_admin"
                      ? "bg-purple-100 text-purple-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {user.tenantRole === "tenant_admin" ? "Admin" : "Uzytkownik"}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {user.isActive ? "Aktywny" : "Nieaktywny"}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(user.joinedAt).toLocaleDateString("pl-PL")}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                {canImpersonate && (
                  <button
                    onClick={() => onImpersonate(user.userId)}
                    disabled={isImpersonating}
                    className="text-amber-600 hover:text-amber-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Zaloguj sie jako ten uzytkownik"
                  >
                    {isImpersonating ? "..." : "Impersonuj"}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Hotels Tab Component
const HotelsTab: React.FC<{
  hotels: TenantHotel[];
  isLoading: boolean;
}> = ({ hotels, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (hotels.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Brak hoteli w tym tenancie
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {hotels.map((hotel) => (
        <div
          key={hotel.id}
          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-medium text-gray-900">{hotel.name}</h3>
            <span
              className={`px-2 py-0.5 text-xs rounded-full ${
                hotel.isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {hotel.isActive ? "Aktywny" : "Nieaktywny"}
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-2">
            {hotel.subdomain}.hotelb2b.com
          </p>
          {hotel.customDomains && hotel.customDomains.length > 0 && (
            <p className="text-xs text-gray-400 mb-2">
              Domeny: {hotel.customDomains.join(", ")}
            </p>
          )}
          <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
            <span className="text-sm text-gray-600">
              <strong>{hotel.offersCount}</strong> ofert
            </span>
            <span className="text-xs text-gray-400">
              Utworzony: {new Date(hotel.createdAt).toLocaleDateString("pl-PL")}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// SUBSCRIPTIONS TAB
// =============================================================================

interface SubscriptionsTabProps {
  tenant: Tenant;
  query: ReturnType<typeof useBillingSubscriptionsQuery>;
}

/**
 * Subscriptions tab — reads from billing endpoint filtered by tenantId.
 *
 * Behaviours:
 * - AC-S3: uses `useBillingSubscriptionsQuery(tenant.brandId, { tenantId, limit, offset })`.
 * - AC-S9: legacy-only tenants → info banner.
 * - AC-C2: empty state via `EmptyState`.
 * - AC-C4/C5: loading spinner + error retry.
 * - AC-C7: SUPERSEDED rows rendered with `opacity-60`.
 * - AC-C8: focus returns to the row that opened the modal.
 * - AC-C9: tenant column omitted (`hideTenantColumn={true}` semantics inlined).
 */
const SubscriptionsTab: React.FC<SubscriptionsTabProps> = ({ tenant, query }) => {
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    string | null
  >(null);
  const [migratingSubscriptionId, setMigratingSubscriptionId] = useState<
    string | null
  >(null);

  // AC-C8: focus return — row-based focus tracking via shared hook (H-CQ-3).
  const {
    setTriggerRef: setRowTriggerRef,
    recordTrigger: recordRowTrigger,
    restoreFocus: restoreRowFocus,
    getTriggerRef: getRowTriggerRef,
  } = useFocusReturn<HTMLTableRowElement>();

  // C3: focus return for migration modal — per-row "Migruj" button refs.
  // NOTE: kept as a separate ref map (not via useFocusReturn) because the
  // migrate-button focus target was added by the UX agent in Wave 1 and
  // coexists with the row trigger refs for the same set of subscription ids.
  const migrateButtonRefs = useRef<Map<string, HTMLButtonElement | null>>(
    new Map()
  );
  // Tracks whether migration was triggered from the detail modal (true) or
  // directly from the row "Migruj" button (false). When detail-triggered,
  // we fall back to focusing the row trigger via the shared hook.
  const migrationFromDetailRef = useRef<boolean>(false);

  const handleCloseDetail = () => {
    setSelectedSubscriptionId(null);
    // Defer focus to next tick so the modal is unmounted.
    restoreRowFocus();
  };

  const handleCloseMigration = () => {
    const id = migratingSubscriptionId;
    const fromDetail = migrationFromDetailRef.current;
    setMigratingSubscriptionId(null);
    migrationFromDetailRef.current = false;
    if (id) {
      setTimeout(() => {
        if (fromDetail) {
          getRowTriggerRef(id)?.focus();
        } else {
          migrateButtonRefs.current.get(id)?.focus();
        }
      }, 0);
    }
  };

  if (!tenant.brandId) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200">
        <EmptyState
          title="Billing niedostępny"
          description="Tenant nie ma przypisanego brandu — billing niedostępny."
        />
      </div>
    );
  }

  // AC-S9: legacy-only tenant banner.
  const isLegacyOnly = tenant.activeSubscription?.source === "legacy";

  if (query.isError) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600">Wystąpił błąd</p>
        <button
          onClick={() => query.refetch()}
          className="mt-2 text-primary-700 underline"
        >
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const subscriptions = query.data?.data ?? [];

  return (
    <div className="space-y-4">
      {isLegacyOnly && subscriptions.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
          Brak subskrypcji w modelu billing (tenant nie został zmigrowany)
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="bg-white rounded-lg border border-neutral-200">
          <EmptyState
            title="Brak subskrypcji"
            description="Ten tenant nie posiada żadnych subskrypcji billing."
          />
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              {/* AC-C9: tenant column hidden in tenant-context view. */}
              <SubscriptionTableHead hideTenantColumn />
              <tbody className="bg-white divide-y divide-neutral-200">
                {subscriptions.map((sub: BillingSubscription) => (
                  <SubscriptionRow
                    key={sub.id}
                    subscription={sub}
                    hideTenantColumn
                    rowRef={setRowTriggerRef(sub.id)}
                    migrateButtonRef={(el) => {
                      migrateButtonRefs.current.set(sub.id, el);
                    }}
                    onSelectDetail={(id) => {
                      recordRowTrigger(id);
                      setSelectedSubscriptionId(id);
                    }}
                    onMigrate={(id) => {
                      migrationFromDetailRef.current = false;
                      setMigratingSubscriptionId(id);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedSubscriptionId && tenant.brandId && (
        <SubscriptionDetailModal
          subscriptionId={selectedSubscriptionId}
          brandId={tenant.brandId}
          onClose={handleCloseDetail}
          onMigrate={() => {
            migrationFromDetailRef.current = true;
            setMigratingSubscriptionId(selectedSubscriptionId);
            setSelectedSubscriptionId(null);
          }}
        />
      )}

      {migratingSubscriptionId && (
        <TenantMigrationModal
          tenantId={tenant.id}
          tenantName={tenant.name}
          onClose={handleCloseMigration}
        />
      )}
    </div>
  );
};

// =============================================================================
// ORDERS TAB
// =============================================================================

interface OrdersTabProps {
  tenant: Tenant;
  query: ReturnType<typeof useBillingOrdersQuery>;
}

const OrdersTab: React.FC<OrdersTabProps> = ({ tenant, query }) => {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Row-based focus return via shared hook (H-CQ-3).
  const {
    setTriggerRef: setRowTriggerRef,
    recordTrigger: recordRowTrigger,
    restoreFocus: restoreRowFocus,
  } = useFocusReturn<HTMLTableRowElement>();
  // C3: focus return for create-order modal — single button, kept as-is.
  const createOrderBtnRef = useRef<HTMLButtonElement>(null);

  const handleCloseDetail = () => {
    setSelectedOrderId(null);
    restoreRowFocus();
  };

  const handleCloseCreate = () => {
    setShowCreateModal(false);
    setTimeout(() => createOrderBtnRef.current?.focus(), 0);
  };

  if (!tenant.brandId) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200">
        <EmptyState
          title="Billing niedostępny"
          description="Tenant nie ma przypisanego brandu — billing niedostępny."
        />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600">Wystąpił błąd</p>
        <button
          onClick={() => query.refetch()}
          className="mt-2 text-primary-700 underline"
        >
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const orders = query.data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          ref={createOrderBtnRef}
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium transition-colors"
        >
          Stwórz zamówienie
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-lg border border-neutral-200">
          <EmptyState
            title="Brak zamówień"
            description="Ten tenant nie posiada żadnych zamówień billing."
          />
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              {/* AC-C9: tenant column hidden in tenant-context view. */}
              <OrderTableHead hideTenantColumn />
              <tbody className="bg-white divide-y divide-neutral-200">
                {orders.map((order: BillingOrder) => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    hideTenantColumn
                    rowRef={setRowTriggerRef(order.id)}
                    onSelectDetail={(id) => {
                      recordRowTrigger(id);
                      setSelectedOrderId(id);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedOrderId && tenant.brandId && (
        <OrderDetailModal
          orderId={selectedOrderId}
          brandId={tenant.brandId}
          onClose={handleCloseDetail}
        />
      )}

      {showCreateModal && tenant.brandId && (
        <CreateOrderModal
          brandId={tenant.brandId}
          preselectTenantId={tenant.id}
          onClose={handleCloseCreate}
          // AC-C12: useCreateBillingOrderMutation already invalidates the
          // orders prefix (BILLING_ADMIN_KEYS.orders(brandId)), which naturally
          // covers the tenantId-scoped query key.
          onSuccess={handleCloseCreate}
        />
      )}
    </div>
  );
};

// Marketing & Sales Tab Component — attribution fields (clientSource + future params)
const MarketingTab: React.FC<{ tenant: Tenant }> = ({ tenant }) => {
  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-4">Atrybucja źródła</h3>
        <MarketingSalesForm tenant={tenant} />
      </div>
    </div>
  );
};

// Settings Tab Component — container orchestrating 3 subcomponents (B1)
const SettingsTab: React.FC<{
  tenant: Tenant;
}> = ({ tenant }) => {
  return (
    <div className="space-y-6">
      {/* Basic info + brand + subdomain edit */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-4">Informacje podstawowe</h3>
        <p className="text-xs text-gray-500 mb-4 font-mono">ID: {tenant.id}</p>
        <TenantBasicInfoForm tenant={tenant} />
      </div>

      {/* Custom domains */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-4">Custom Domains</h3>
        <CustomDomainsManager tenant={tenant} />
      </div>

      {/* Module toggles */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <ModuleManager tenant={tenant} />
      </div>
    </div>
  );
};

export default TenantDetails;
